import { ProjectComponentType } from "../enums/project-component-type.enum";
import { ProjectStatusCode } from "../enums/project-status-code.enum";
import { IItem } from "../interfaces/items/item.interface";
import { IProjectButtonPanelHeading } from "../interfaces/project/project-button-panel-heading.interface";
import { IProjectComponent } from "../interfaces/project/project-component.interface";
import { IProjectPrice } from "../interfaces/project/project-price.interface";
import { IProjectSparePart } from "../interfaces/project/project-spare-part.interface";
import { IProject } from "../interfaces/project/project.interface";
import { IWSDateTime } from "../interfaces/ws-datetime.interface";
import { ProjectButtonPanel } from "./project-button-panel";
import { ProjectSparePart } from "./project-spare-part";
import { WSDateTime } from "./ws-datetime";

export class Project implements IProject {

    public price?: IProjectPrice;
    public statusCode = '';
    public isLocked = false;

    get isDraft(): boolean {
        return this.statusCode === ProjectStatusCode.DRAFT;
    }

    get idProject(): number {
        return this._idProject;
    }

    get creationDate(): WSDateTime {
        return this._creationDate;
    }

    get uuid(): string {
        return this._uuid;
    }

    get revision(): number {
        return this._revision;
    }

    get idStatus(): number {
        return this._idStatus;
    }

    get statusLabel(): string {
        return this._statusLabel;
    }
    get statusColor(): string {
        return this._statusColor;
    }

    get buttonPanels(): ProjectButtonPanel[] {
        return this._buttonPanels;
    }

    get spareParts(): ProjectSparePart[] {
        return this._spareParts;
    }

    get buttonPanelsForDashboardSection(): IProjectComponent[] {
        return this._buttonPanels.map(buttonPanel => buttonPanel.formatToSectionItem());
    }

    get sparePartsForDashboardSection(): IProjectComponent[] {
        return this._spareParts.map(sparePart => sparePart.formatToSectionItem());
    }

    get sparePartsForSummary(): IItem[] {
        return this._spareParts.map(sparePart => sparePart.formatToItem());
    }

    constructor(
        private _idProject: number,
        private _creationDate: WSDateTime,
        public updateDate: WSDateTime,
        public title: string,
        public idCustomer: number,
        public customerBusinessName: string,
        private _uuid: string,
        public note: string | undefined,
        public deliveryDate: WSDateTime,
        private _revision: number,
        private _idStatus: number,
        private _statusLabel: string,
        private _statusColor: string,
        private _buttonPanels: ProjectButtonPanel[],
        private _spareParts: ProjectSparePart[]
    ) { }

    public addButtonPanel(idProjectButtonPanel: number, name: string, quantity: number, completed: boolean = false): void {
        this._buttonPanels.push(new ProjectButtonPanel(
            idProjectButtonPanel,
            name,
            completed,
            quantity
        ));
    }

    public updateButtonPanel(idProjectButtonPanel: number, name: string, quantity: number) {
        const buttonPanel = this._buttonPanels.find(buttonPanel => buttonPanel.idProjectButtonPanel === idProjectButtonPanel);
        if (buttonPanel) {
            buttonPanel.name = name;
            buttonPanel.setQuantity(quantity);
        }
    }

    public deleteButtonPanel(idProjectButtonPanel: number): void {
        this._buttonPanels = this._buttonPanels.filter(buttonPanel => buttonPanel.idProjectButtonPanel !== idProjectButtonPanel);
    }

    public setSpareParts(spareParts: IProjectSparePart[]): void {
        this._spareParts = Project.getSpareParts(spareParts);
    }

    public updateSparePart(idProjectSparePart: number, quantity: number): void {
        const sparePart = this._spareParts.find(sparePart => sparePart.idProjectSparePart === idProjectSparePart);
        if (sparePart) {
            sparePart.setQuantity(quantity);
        }
    }

    public deleteSparePart(idProjectSparePart: number): void {
        this._spareParts = this._spareParts.filter(sparePart => sparePart.idProjectSparePart != idProjectSparePart);
    }

    public setUpdateDate(updateDate: IWSDateTime): void {
        this.updateDate = WSDateTime.from(updateDate);
    }

    public getComponentTitle(type: ProjectComponentType, idComponent: number): string {
        switch(type) {
            case ProjectComponentType.BUTTONPANEL:
                return this._buttonPanels.find(buttonPanel => buttonPanel.idProjectButtonPanel === idComponent)?.getComponentTitle() ?? "";
            default:
                return this._spareParts.find(sparePart => sparePart.idProjectSparePart === idComponent)?.getComponentTitle() ?? "";
        }
    }

    static from(value: IProject): Project {
        const project = new Project(
            value.idProject,
            WSDateTime.from(value.creationDate),
            WSDateTime.from(value.updateDate),
            value.title,
            value.idCustomer,
            value.customerBusinessName,
            value.uuid,
            value.note,
            WSDateTime.from(value.deliveryDate),
            value.revision,
            value.idStatus,
            value.statusLabel,
            value.statusColor,
            this.getButtonPanels(value.buttonPanels),
            this.getSpareParts(value.spareParts)
        );
        project.price = value.price;
        project.statusCode = value.statusCode;
        project.isLocked = value.isLocked;
        return project;
    }

    private static getButtonPanels(value: IProjectButtonPanelHeading[]): ProjectButtonPanel[] {
        let buttonPanels: ProjectButtonPanel[] = [];
        value.forEach(buttonPanel => {
            if (ProjectButtonPanel.isProjectButtonPanelHeading(buttonPanel)) {
                buttonPanels.push(ProjectButtonPanel.fromHeading(buttonPanel))
            } else if (ProjectButtonPanel.isProjectButtonPanel(buttonPanel)) {
                buttonPanels.push(ProjectButtonPanel.from(buttonPanel));
            }
        });
        return buttonPanels;
    }

    private static getSpareParts(value: IProjectSparePart[]): ProjectSparePart[] {
        let spareParts: ProjectSparePart[] = [];
        value.forEach(sparePart => {
            spareParts.push(ProjectSparePart.from(sparePart))
        });
        return spareParts;
    }
}
